/**
 * Procedural nature for the outdoor world.
 *
 * Phase 1 (small pond + nearby groves) was the first pass.  Phase 2 (this
 * file) is the world-filling pass: a **massive lake** dominates the south,
 * with bays, an island and wooden docks; dense oak / birch / pine forests
 * wrap around the keep; multiple meadows dot the landscape; wildlife
 * (deer, birds) and forest detail (mushrooms, ferns, fallen logs,
 * standing stones) fill the gaps.
 *
 * Everything stays procedural (box meshes + canvas-painted pixel
 * textures) per VISUAL_STYLE.md: no PBR, NEAREST sampling, deterministic
 * mulberry32 PRNGs for stable layouts across reloads.
 *
 * Goblin patrol bounds (server-side: −12..12, −10..10) are kept clear of
 * any tree trunk or rock so the AI tick never runs into collision
 * geometry.
 *
 * Performance: trees use regular meshes (~145 total, most off-screen
 * thanks to fog); foliage instances (grass / flowers / reeds / lily
 * pads / mushrooms / ferns) use Mesh.createInstance so the GPU batches
 * the draw calls per species.
 */

import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { Scene } from '@babylonjs/core/scene';

import {
  birchBarkTexture,
  birchLeavesTexture,
  birdTexture,
  deerTexture,
  fallenLogTexture,
  fernTexture,
  flowerSpriteTexture,
  grassBladeTexture,
  lakeWaterTexture,
  lilyPadTexture,
  meadowGrassTexture,
  menhirTexture,
  mushroomTexture,
  oakLeavesTexture,
  pineLeavesTexture,
  reedTexture,
  rockTexture,
  sandTexture,
  timberBeamTexture,
  treeBarkTexture,
  wildGrassTexture,
  woodPlankTexture,
} from './textures';

// ===========================================================================
// PRNG + small helpers
// ===========================================================================

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeMat(scene: Scene, name: string): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.specularColor = new Color3(0, 0, 0);
  return m;
}

// ===========================================================================
// Trees
// ===========================================================================

interface TreeOpts {
  x: number;
  z: number;
  rotateY?: number;
  scale?: number;
}

function trunkBox(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  width: number,
  height: number,
  depth: number,
  barkTex: Texture,
  x: number,
  z: number,
  rotateY: number
): Mesh {
  const trunk = MeshBuilder.CreateBox(
    `trunk-${x.toFixed(2)}-${z.toFixed(2)}`,
    { width, height, depth },
    scene
  );
  trunk.position.set(x, height / 2, z);
  trunk.rotation.y = rotateY;
  trunk.parent = parent;
  const tm = makeMat(scene, `trunk-mat-${Math.random()}`);
  tm.diffuseTexture = barkTex;
  trunk.material = tm;
  trunk.receiveShadows = true;
  shadowGen.addShadowCaster(trunk);
  trunk.checkCollisions = true;
  return trunk;
}

interface OakExtraOpts extends TreeOpts {
  canopyRadius?: number;
}

function buildOak(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  bark: Texture,
  leaves: Texture,
  opts: OakExtraOpts
): Mesh {
  const r = opts.canopyRadius ?? 2.2;
  const root = new Mesh(`oak-${opts.x.toFixed(1)}-${opts.z.toFixed(1)}`, scene);
  root.parent = parent;
  root.position.set(opts.x, 0, opts.z);

  const lowerH = 2.2;
  trunkBox(scene, shadowGen, root, 0.55, lowerH, 0.55, bark, 0, 0, opts.rotateY ?? 0);
  const upperH = 1.4;
  trunkBox(scene, shadowGen, root, 0.4, upperH, 0.4, bark, 0, 0, opts.rotateY ?? 0).position.y =
    lowerH + upperH / 2;

  const leafMat = makeMat(scene, 'oak-leaves-mat');
  leafMat.diffuseTexture = leaves;
  const rnd = mulberry32(Math.floor(opts.x * 100 + opts.z * 7 + 11));
  const canopyY = lowerH + upperH + r * 0.55;
  const boxCount = 6 + Math.floor(rnd() * 3);
  for (let i = 0; i < boxCount; i++) {
    const bw = r * (0.9 + rnd() * 0.7);
    const bh = r * (0.7 + rnd() * 0.6);
    const bd = r * (0.9 + rnd() * 0.7);
    const box = MeshBuilder.CreateBox(`oak-leaf-${i}`, { width: bw, height: bh, depth: bd }, scene);
    box.position.set((rnd() - 0.5) * r * 1.4, canopyY + (rnd() - 0.5) * r * 0.7, (rnd() - 0.5) * r * 1.4);
    box.parent = root;
    box.material = leafMat;
    box.receiveShadows = true;
    shadowGen.addShadowCaster(box);
  }
  if (opts.scale) root.scaling.set(opts.scale, opts.scale, opts.scale);
  return root;
}

function buildBirch(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  bark: Texture,
  leaves: Texture,
  opts: TreeOpts
): Mesh {
  const root = new Mesh(`birch-${opts.x.toFixed(1)}-${opts.z.toFixed(1)}`, scene);
  root.parent = parent;
  root.position.set(opts.x, 0, opts.z);

  trunkBox(scene, shadowGen, root, 0.32, 4.2, 0.32, bark, 0, 0, opts.rotateY ?? 0);

  const leafMat = makeMat(scene, 'birch-leaves-mat');
  leafMat.diffuseTexture = leaves;
  const rnd = mulberry32(Math.floor(opts.x * 100 + opts.z * 7 + 23));
  const baseY = 4.2 + 0.3;
  const boxCount = 2 + Math.floor(rnd() * 2);
  for (let i = 0; i < boxCount; i++) {
    const bw = 1.0 + rnd() * 0.8;
    const bh = 1.0 + rnd() * 0.5;
    const bd = 1.0 + rnd() * 0.8;
    const box = MeshBuilder.CreateBox(`birch-leaf-${i}`, { width: bw, height: bh, depth: bd }, scene);
    box.position.set((rnd() - 0.5) * 1.4, baseY + (rnd() - 0.5) * 0.8, (rnd() - 0.5) * 1.4);
    box.parent = root;
    box.material = leafMat;
    box.receiveShadows = true;
    shadowGen.addShadowCaster(box);
  }
  if (opts.scale) root.scaling.set(opts.scale, opts.scale, opts.scale);
  return root;
}

interface PineExtraOpts extends TreeOpts {
  tiers?: number;
}

function buildPine(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  bark: Texture,
  leaves: Texture,
  opts: PineExtraOpts
): Mesh {
  const root = new Mesh(`pine-${opts.x.toFixed(1)}-${opts.z.toFixed(1)}`, scene);
  root.parent = parent;
  root.position.set(opts.x, 0, opts.z);

  trunkBox(scene, shadowGen, root, 0.35, 2.5, 0.35, bark, 0, 0, opts.rotateY ?? 0);

  const leafMat = makeMat(scene, 'pine-leaves-mat');
  leafMat.diffuseTexture = leaves;
  const tiers = opts.tiers ?? 3;
  const tierH = 1.6;
  let y = 2.5;
  let w = 2.4;
  for (let i = 0; i < tiers; i++) {
    const box = MeshBuilder.CreateBox(
      `pine-tier-${i}`,
      { width: w, height: tierH, depth: w },
      scene
    );
    box.position.set(0, y + tierH / 2, 0);
    box.parent = root;
    box.material = leafMat;
    box.receiveShadows = true;
    shadowGen.addShadowCaster(box);
    y += tierH * 0.6;
    w *= 0.72;
  }
  if (opts.scale) root.scaling.set(opts.scale, opts.scale, opts.scale);
  return root;
}

// ===========================================================================
// Decorative forest elements
// ===========================================================================

/** Fallen log — long thin box lying on its side, with bark texture. */
function placeLog(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  logTex: Texture,
  x: number,
  z: number,
  length: number,
  rotationY: number
): void {
  const log = MeshBuilder.CreateBox('log', { width: length, height: 0.4, depth: 0.45 }, scene);
  log.position.set(x, 0.2, z);
  log.rotation.y = rotationY;
  log.parent = parent;
  const m = makeMat(scene, 'log-mat');
  m.diffuseTexture = logTex;
  log.material = m;
  log.receiveShadows = true;
  shadowGen.addShadowCaster(log);
  log.checkCollisions = true;
}

/** Standing stone (menhir) — vertical slab. */
function placeMenhir(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  tex: Texture,
  x: number,
  z: number,
  height: number,
  rotationY: number
): void {
  const stone = MeshBuilder.CreateBox('menhir', { width: 1.2, height, depth: 0.6 }, scene);
  stone.position.set(x, height / 2, z);
  stone.rotation.y = rotationY;
  stone.parent = parent;
  const m = makeMat(scene, 'menhir-mat');
  m.diffuseTexture = tex;
  stone.material = m;
  stone.receiveShadows = true;
  shadowGen.addShadowCaster(stone);
  stone.checkCollisions = true;
}

// ===========================================================================
// Rocks (path + forest edge + meadow edge + lake shore)
// ===========================================================================

function placeRocks(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  rockTex: Texture,
  positions: Array<{ x: number; z: number; w: number; h: number; d: number; rot?: number }>
): void {
  if (positions.length === 0) return;
  const rmat = makeMat(scene, 'rock-mat');
  rmat.diffuseTexture = rockTex;
  for (const p of positions) {
    const rock = MeshBuilder.CreateBox(
      `rock-${p.x.toFixed(1)}-${p.z.toFixed(1)}`,
      { width: p.w, height: p.h, depth: p.d },
      scene
    );
    rock.position.set(p.x, p.h / 2, p.z);
    rock.rotation.y = p.rot ?? 0;
    rock.parent = parent;
    rock.material = rmat;
    rock.receiveShadows = true;
    shadowGen.addShadowCaster(rock);
    rock.checkCollisions = true;
  }
}

// ===========================================================================
// Instanced foliage (grass / flowers / reeds / lily pads / mushrooms / ferns)
// ===========================================================================

function makeFoliageInstances(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  name: string,
  tex: Texture,
  positions: Array<{ x: number; z: number; y?: number; scale?: number; rot?: number }>,
  width: number,
  height: number
): InstancedMesh[] {
  if (positions.length === 0) return [];
  const master = MeshBuilder.CreatePlane(name, { width, height }, scene);
  master.parent = parent;
  master.position.set(0, height / 2, 0);
  master.billboardMode = AbstractMesh.BILLBOARDMODE_Y;
  master.isPickable = false;
  const m = makeMat(scene, `${name}-mat`);
  m.diffuseTexture = tex;
  m.emissiveTexture = tex;
  m.useAlphaFromDiffuseTexture = true;
  m.diffuseTexture.hasAlpha = true;
  m.alphaCutOff = 0.4;
  m.backFaceCulling = false;
  m.disableLighting = true;
  master.material = m;
  master.receiveShadows = false;
  // Hide the master by parking it below the world.  setEnabled(false)
  // would also kill the geometry shader path that the instances rely on.
  master.position.y = -1000;
  master.visibility = 0;

  const instances: InstancedMesh[] = [];
  for (const p of positions) {
    const inst = master.createInstance(`${name}-inst`);
    inst.parent = parent;
    inst.position.set(p.x, (height * (p.scale ?? 1)) / 2, p.z);
    inst.rotation.y = p.rot ?? 0;
    inst.scaling.set(p.scale ?? 1, p.scale ?? 1, p.scale ?? 1);
    inst.isPickable = false;
    instances.push(inst);
  }
  shadowGen.addShadowCaster(master as AbstractMesh);
  return instances;
}

// ===========================================================================
// Wildlife billboards (deer)
// ===========================================================================

function placeDeer(
  scene: Scene,
  parent: Mesh,
  deerTex: Texture,
  positions: Array<{ x: number; z: number; rot?: number }>
): Mesh[] {
  const mat = makeMat(scene, 'deer-mat');
  mat.diffuseTexture = deerTex;
  mat.useAlphaFromDiffuseTexture = true;
  mat.diffuseTexture.hasAlpha = true;
  mat.emissiveTexture = deerTex;
  mat.disableLighting = true;
  mat.backFaceCulling = false;
  mat.alphaCutOff = 0.4;
  // (using local `mat` is intentional and fine — it shadows the helper)

  const out: Mesh[] = [];
  for (const p of positions) {
    const plane = MeshBuilder.CreatePlane('deer', { width: 2.0, height: 2.0 }, scene);
    plane.position.set(p.x, 1.0, p.z);
    plane.rotation.y = p.rot ?? 0;
    plane.billboardMode = AbstractMesh.BILLBOARDMODE_Y;
    plane.parent = parent;
    plane.material = mat;
    plane.isPickable = false;
    out.push(plane);
  }
  return out;
}

// ===========================================================================
// Massive lake (Phase 2)
// ===========================================================================

interface LakeHandles {
  /** Update hook — called per frame from main.ts for the UV scroll. */
  update: (dtSec: number) => void;
}

/**
 * Build the massive central lake south of the keep.  Shape:
 *
 *       ┌──────────────────┐  N
 *   bay │                  │   ↑
 *       └──┐             ┌─┘   S (down)
 *          │   ┌─────┐   │
 *          │   │island│  │
 *          │   └─────┘   │
 *         ┌┘             └┐
 *         │      (main)    │
 *         └────────────────┘
 *              bay
 *
 * The "shore" gets a sand texture transition strip just inside the
 * northern edge, and the lake surface uses a deeper blue with subtle
 * sin-driven UV-offset ripples + Phase A/B crossfade every 1.5 s.
 */
function buildMassiveLake(scene: Scene, parent: Mesh): LakeHandles {
  // ---- Main lake body ----
  const main = MeshBuilder.CreateGround(
    'lake-main',
    { width: 90, height: 80, subdivisions: 6 },
    scene
  );
  main.position.set(0, -0.05, -52);
  main.parent = parent;
  const mainMat = makeMat(scene, 'lake-main-mat');
  const texA = lakeWaterTexture(scene, 70);
  const texB = lakeWaterTexture(scene, 71);
  texA.uScale = 6;
  texA.vScale = 6;
  texB.uScale = 6;
  texB.vScale = 6;
  mainMat.diffuseTexture = texA;
  mainMat.specularColor = new Color3(0.45, 0.55, 0.65);
  mainMat.specularPower = 64;
  mainMat.emissiveColor = new Color3(0.05, 0.08, 0.12);
  main.material = mainMat;
  main.receiveShadows = true;
  // Water surface: no collision — the player walks on the ground beneath.

  // ---- SW bay ----
  const baySw = MeshBuilder.CreateGround(
    'lake-bay-sw',
    { width: 24, height: 28, subdivisions: 4 },
    scene
  );
  baySw.position.set(-32, -0.05, -38);
  baySw.parent = parent;
  const baySwMat = makeMat(scene, 'lake-bay-sw-mat');
  baySwMat.diffuseTexture = lakeWaterTexture(scene, 72);
  (baySwMat.diffuseTexture as Texture).uScale = 3;
  (baySwMat.diffuseTexture as Texture).vScale = 3;
  baySwMat.specularColor = new Color3(0.4, 0.5, 0.6);
  baySwMat.specularPower = 48;
  baySwMat.emissiveColor = new Color3(0.05, 0.08, 0.12);
  baySw.material = baySwMat;
  baySw.receiveShadows = true;

  // ---- SE bay ----
  const baySe = MeshBuilder.CreateGround(
    'lake-bay-se',
    { width: 20, height: 22, subdivisions: 4 },
    scene
  );
  baySe.position.set(36, -0.05, -42);
  baySe.parent = parent;
  const baySeMat = makeMat(scene, 'lake-bay-se-mat');
  baySeMat.diffuseTexture = lakeWaterTexture(scene, 73);
  (baySeMat.diffuseTexture as Texture).uScale = 3;
  (baySeMat.diffuseTexture as Texture).vScale = 3;
  baySeMat.specularColor = new Color3(0.4, 0.5, 0.6);
  baySeMat.specularPower = 48;
  baySeMat.emissiveColor = new Color3(0.05, 0.08, 0.12);
  baySe.material = baySeMat;
  baySe.receiveShadows = true;

  // ---- Northern shallows (lighter blue transition) ----
  const shallows = MeshBuilder.CreateGround(
    'lake-shallows',
    { width: 86, height: 6, subdivisions: 4 },
    scene
  );
  shallows.position.set(0, -0.04, -14);
  shallows.parent = parent;
  const shallowsMat = makeMat(scene, 'lake-shallows-mat');
  const shallowsTex = lakeWaterTexture(scene, 74);
  shallowsTex.uScale = 8;
  shallowsTex.vScale = 1;
  shallowsMat.diffuseTexture = shallowsTex;
  shallowsMat.specularColor = new Color3(0.5, 0.6, 0.7);
  shallowsMat.emissiveColor = new Color3(0.08, 0.12, 0.18);
  shallows.material = shallowsMat;
  shallows.receiveShadows = true;

  // ---- Sand beach along the northern shore ----
  const beach = MeshBuilder.CreateGround(
    'lake-beach',
    { width: 70, height: 2.5, subdivisions: 4 },
    scene
  );
  beach.position.set(0, 0.01, -11);
  beach.parent = parent;
  const beachMat = makeMat(scene, 'lake-beach-mat');
  beachMat.diffuseTexture = sandTexture(scene);
  (beachMat.diffuseTexture as Texture).uScale = 8;
  (beachMat.diffuseTexture as Texture).vScale = 1;
  beach.material = beachMat;
  beach.receiveShadows = true;
  beach.checkCollisions = true;

  // ---- Island in the middle ----
  const island = MeshBuilder.CreateGround(
    'lake-island',
    { width: 9, height: 6, subdivisions: 4 },
    scene
  );
  island.position.set(-4, 0.02, -32);
  island.parent = parent;
  const islandMat = makeMat(scene, 'lake-island-mat');
  islandMat.diffuseTexture = sandTexture(scene);
  (islandMat.diffuseTexture as Texture).uScale = 2;
  (islandMat.diffuseTexture as Texture).vScale = 2;
  island.material = islandMat;
  island.receiveShadows = true;
  island.checkCollisions = true;

  // Sand rim around the island
  const islandRim = MeshBuilder.CreateGround(
    'lake-island-rim',
    { width: 11, height: 8, subdivisions: 4 },
    scene
  );
  islandRim.position.set(-4, 0.005, -32);
  islandRim.parent = parent;
  const rimMat = makeMat(scene, 'lake-island-rim-mat');
  rimMat.diffuseTexture = sandTexture(scene);
  (rimMat.diffuseTexture as Texture).uScale = 3;
  (rimMat.diffuseTexture as Texture).vScale = 2;
  islandRim.material = rimMat;
  islandRim.receiveShadows = true;
  islandRim.checkCollisions = true;

  // ---- UV-scroll animation ----
  let t = 0;
  let phase = false;
  return {
    update(dtSec: number) {
      t += dtSec;
      const cur = phase ? texB : texA;
      cur.uOffset = Math.sin(t * 0.35) * 0.06;
      cur.vOffset = Math.cos(t * 0.25) * 0.06;
      if (t > 1.5) {
        t = 0;
        phase = !phase;
        mainMat.diffuseTexture = phase ? texB : texA;
      }
    },
  };
}

// ===========================================================================
// Wooden docks
// ===========================================================================

function buildDock(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  cx: number,
  cz: number,
  widthX: number,
  depthZ: number,
  rotationY: number,
  plankTex: Texture,
  beamTex: Texture
): void {
  const root = new TransformNode(`dock-${cx.toFixed(1)}-${cz.toFixed(1)}`, scene);
  root.parent = parent;
  root.position.set(cx, 0, cz);
  root.rotation.y = rotationY;

  const plankMat = makeMat(scene, 'dock-plank-mat');
  plankMat.diffuseTexture = plankTex;
  const beamMat = makeMat(scene, 'dock-beam-mat');
  beamMat.diffuseTexture = beamTex;

  // 2 longitudinal beams under the dock
  for (const xOff of [-widthX / 2 + 0.2, widthX / 2 - 0.2]) {
    const beam = MeshBuilder.CreateBox('dock-beam', { width: widthX, height: 0.2, depth: 0.22 }, scene);
    beam.position.set(0, 0.1, xOff);
    beam.parent = root;
    beam.material = beamMat;
    beam.receiveShadows = true;
    shadowGen.addShadowCaster(beam);
    beam.checkCollisions = true;
  }
  // Planks across
  const plankCount = Math.max(3, Math.floor(widthX / 0.5));
  for (let i = 0; i < plankCount; i++) {
    const px = -widthX / 2 + (i + 0.5) * (widthX / plankCount);
    const plank = MeshBuilder.CreateBox(
      'dock-plank',
      { width: widthX / plankCount, height: 0.1, depth: 1.1 },
      scene
    );
    plank.position.set(px, 0.25, 0);
    plank.parent = root;
    plank.material = plankMat;
    plank.receiveShadows = true;
    shadowGen.addShadowCaster(plank);
    plank.checkCollisions = true;
  }
  // 4 posts (pilings) at the corners — in the water
  for (const px of [-widthX / 2 + 0.2, widthX / 2 - 0.2]) {
    for (const pz of [-depthZ / 2 + 0.2, depthZ / 2 - 0.2]) {
      const post = MeshBuilder.CreateBox('dock-post', { width: 0.18, height: 0.6, depth: 0.18 }, scene);
      post.position.set(px, -0.2, pz);
      post.parent = root;
      post.material = beamMat;
      post.receiveShadows = true;
      shadowGen.addShadowCaster(post);
    }
  }
}

// ===========================================================================
// Public entry point
// ===========================================================================

export interface NatureHandles {
  /** Update hook for animated water — call once per frame. */
  update: (dtSec: number) => void;
  dispose: () => void;
}

/**
 * Static world layout — exposed for the minimap / full-map HUD so it can
 * draw the big landmarks (lake + bays, forest regions, buildings, docks,
 * menhirs) without re-deriving them from mesh positions.
 *
 * Coords are in world XZ space; the minimap projects them onto a 2D
 * canvas with X→X and Z→Y (top-down view).
 */
export interface WorldStaticLayout {
  lake: {
    main: { x: number; z: number; w: number; h: number };
    bays: Array<{ x: number; z: number; w: number; h: number }>;
    shallows: { x: number; z: number; w: number; h: number };
    beach: { x: number; z: number; w: number; h: number };
    island: { x: number; z: number; w: number; h: number };
  };
  forests: Array<{
    kind: 'oak' | 'birch' | 'pine';
    bounds: { x: number; z: number; w: number; h: number };
  }>;
  buildings: Array<{
    kind: 'keep' | 'tower' | 'wall' | 'house';
    bounds: { x: number; z: number; w: number; h: number };
  }>;
  meadows: Array<{ x: number; z: number; w: number; h: number }>;
  docks: Array<{ x: number; z: number; w: number; h: number; rotationY: number }>;
  menhirs: Array<{ x: number; z: number }>;
  worldHalf: number; // half-size of the 200×200 ground (so map can clamp)
}

export const WORLD_LAYOUT: WorldStaticLayout = {
  lake: {
    main: { x: 0, z: -52, w: 90, h: 80 },
    bays: [
      { x: -32, z: -38, w: 24, h: 28 }, // SW bay
      { x: 36, z: -42, w: 20, h: 22 }, // SE bay
    ],
    shallows: { x: 0, z: -14, w: 86, h: 6 },
    beach: { x: 0, z: -11, w: 70, h: 2.5 },
    island: { x: -4, z: -32, w: 11, h: 8 }, // includes the sand rim
  },
  forests: [
    // Hero oaks at the southern curtain wall (foreground depth)
    { kind: 'oak', bounds: { x: -8, z: -13.5, w: 30, h: 2 } },
    // Dense oak forest NW of the keep
    { kind: 'oak', bounds: { x: -39, z: 7, w: 42, h: 45 } },
    // Birch grove SW
    { kind: 'birch', bounds: { x: -22, z: 0, w: 40, h: 40 } },
    // Pine forest NE
    { kind: 'pine', bounds: { x: 38, z: 18, w: 40, h: 45 } },
    // Pine along SW lake shore
    { kind: 'pine', bounds: { x: -61, z: -30, w: 12, h: 30 } },
    // Pine along E lake shore
    { kind: 'pine', bounds: { x: 52, z: -35, w: 15, h: 30 } },
    // Far-distance silhouettes (fog-faded)
    { kind: 'pine', bounds: { x: 0, z: 0, w: 200, h: 200 } },
  ],
  buildings: [
    { kind: 'keep', bounds: { x: 0, z: 6, w: 12, h: 10 } },
    { kind: 'tower', bounds: { x: -8, z: 14, w: 4, h: 4 } },
    { kind: 'tower', bounds: { x: 8, z: 14, w: 4, h: 4 } },
    { kind: 'house', bounds: { x: -12, z: -6, w: 6, h: 5 } }, // showcase
    { kind: 'wall', bounds: { x: 0, z: 22, w: 24, h: 0.4 } }, // N curtain
    { kind: 'wall', bounds: { x: 0, z: -10, w: 24, h: 0.4 } }, // S curtain
    { kind: 'wall', bounds: { x: 16, z: 6, w: 0.4, h: 16 } }, // E curtain
    { kind: 'wall', bounds: { x: -16, z: 6, w: 0.4, h: 16 } }, // W curtain
  ],
  meadows: [
    { x: 28, z: 22, w: 36, h: 30 }, // SE meadow
    { x: -35, z: 18, w: 16, h: 14 }, // NW clearing
  ],
  docks: [
    { x: -20, z: -12, w: 4, h: 4, rotationY: 0 },
    { x: 18, z: -12, w: 5, h: 4, rotationY: 0 },
  ],
  menhirs: [
    { x: -30, z: 35 },
    { x: 38, z: 42 },
    { x: -55, z: -10 },
  ],
  worldHalf: 100, // 200×200 ground
};

/**
 * Build all nature elements.  Parent is the world-root from buildScene.
 * Returns handles that the render loop must update for the lake
 * animation.
 */
export function buildNature(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh
): NatureHandles {
  const oakBark = treeBarkTexture(scene);
  const oakLeaves = oakLeavesTexture(scene);
  const birchBark = birchBarkTexture(scene);
  const birchLeaves = birchLeavesTexture(scene);
  const pineBark = treeBarkTexture(scene);
  const pineLeaves = pineLeavesTexture(scene);
  const meadowTex = meadowGrassTexture(scene);
  const wildTex = wildGrassTexture(scene);
  const flowerTex = flowerSpriteTexture(scene);
  const bladeTex = grassBladeTexture(scene);
  const reedTex = reedTexture(scene);
  const rockTex = rockTexture(scene);
  const lilyTex = lilyPadTexture(scene);
  const mushroomTex = mushroomTexture(scene);
  const fernTex = fernTexture(scene);
  const deerTex = deerTexture(scene);
  const menhirTex = menhirTexture(scene);
  const logTex = fallenLogTexture(scene);

  const disposed: Array<{ dispose: () => void }> = [];
  const allMeshes: Mesh[] = [];
  const lake = buildMassiveLake(scene, parent);

  // =========================================================================
  // 1. Hero oaks along the southern curtain wall (foreground depth)
  // =========================================================================
  const heroOaks = [
    { x: -8, z: -13, r: 2.0 },
    { x: -2, z: -14.5, r: 2.4 },
    { x: 5, z: -13, r: 2.2 },
    { x: 11, z: -14, r: 2.6 },
    { x: -14, z: -13.5, r: 2.0 },
    { x: 14, z: -14, r: 2.3 },
  ];
  for (const o of heroOaks) {
    const t = buildOak(scene, shadowGen, parent, oakBark, oakLeaves, {
      x: o.x,
      z: o.z,
      canopyRadius: o.r,
      rotateY: Math.random() * Math.PI * 2,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 2. Dense oak forest NW of the keep (filling the quadrant)
  // =========================================================================
  const oakNwRnd = mulberry32(100);
  for (let i = 0; i < 35; i++) {
    // Cluster between x=-60..-18 and z=-15..30
    const cx = -60 + oakNwRnd() * 42;
    const cz = -15 + oakNwRnd() * 45;
    // Skip if it would land in the courtyard (x > -16, z > -9)
    if (cx > -16 && cz < 14 && cz > -9) continue;
    const t = buildOak(scene, shadowGen, parent, oakBark, oakLeaves, {
      x: cx,
      z: cz,
      canopyRadius: 1.8 + oakNwRnd() * 0.9,
      rotateY: oakNwRnd() * Math.PI * 2,
      scale: 0.8 + oakNwRnd() * 0.4,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 3. Birch grove SW (extended)
  // =========================================================================
  const birchesRnd = mulberry32(101);
  for (let i = 0; i < 25; i++) {
    const angle = birchesRnd() * Math.PI * 2;
    const r = 18 + birchesRnd() * 16;
    const cx = -Math.cos(angle) * r;
    const cz = Math.sin(angle) * r * 0.6 + 4;
    const t = buildBirch(scene, shadowGen, parent, birchBark, birchLeaves, {
      x: cx,
      z: cz,
      rotateY: birchesRnd() * Math.PI * 2,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 4. Pine forest NE of the keep (deep, dense)
  // =========================================================================
  const pineNeRnd = mulberry32(202);
  for (let i = 0; i < 28; i++) {
    const cx = 18 + pineNeRnd() * 40;
    const cz = -10 + pineNeRnd() * 45;
    // Skip courtyard
    if (cx < 16 && cz > -9 && cz < 14) continue;
    const t = buildPine(scene, shadowGen, parent, pineBark, pineLeaves, {
      x: cx,
      z: cz,
      rotateY: pineNeRnd() * Math.PI * 2,
      tiers: 3 + Math.floor(pineNeRnd() * 2),
    });
    disposed.push(t);
  }

  // =========================================================================
  // 5. Pines along the SW/W lake shore (between forest and water)
  // =========================================================================
  const pineShoreRnd = mulberry32(203);
  for (let i = 0; i < 18; i++) {
    const cz = -15 - pineShoreRnd() * 30;
    const cx = -55 - pineShoreRnd() * 12;
    const t = buildPine(scene, shadowGen, parent, pineBark, pineLeaves, {
      x: cx,
      z: cz,
      rotateY: pineShoreRnd() * Math.PI * 2,
      tiers: 3,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 6. Pines along the eastern lake shore
  // =========================================================================
  const pineEastRnd = mulberry32(204);
  for (let i = 0; i < 14; i++) {
    const cz = -20 - pineEastRnd() * 30;
    const cx = 45 + pineEastRnd() * 15;
    const t = buildPine(scene, shadowGen, parent, pineBark, pineLeaves, {
      x: cx,
      z: cz,
      rotateY: pineEastRnd() * Math.PI * 2,
      tiers: 3,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 7. Far-distance pine silhouettes (fog-faded for depth)
  // =========================================================================
  const farPineRnd = mulberry32(205);
  for (let i = 0; i < 25; i++) {
    // Anywhere within 80-100 m radius, biased W and S
    const angle = farPineRnd() * Math.PI * 2;
    const r = 80 + farPineRnd() * 20;
    const cx = Math.cos(angle) * r;
    const cz = Math.sin(angle) * r;
    const t = buildPine(scene, shadowGen, parent, pineBark, pineLeaves, {
      x: cx,
      z: cz,
      rotateY: farPineRnd() * Math.PI * 2,
      tiers: 3,
    });
    disposed.push(t);
  }

  // =========================================================================
  // 8. Tree on the island (lone hero)
  // =========================================================================
  const islandTree = buildOak(scene, shadowGen, parent, oakBark, oakLeaves, {
    x: -4,
    z: -32,
    canopyRadius: 2.0,
    rotateY: Math.random() * Math.PI * 2,
  });
  disposed.push(islandTree);

  // =========================================================================
  // 9. SE meadow (flower-rich)
  // =========================================================================
  const meadow = MeshBuilder.CreateGround(
    'meadow-se',
    { width: 36, height: 30, subdivisions: 4 },
    scene
  );
  meadow.position.set(28, 0.02, 22);
  meadow.parent = parent;
  const meadowMat = makeMat(scene, 'meadow-se-mat');
  meadowMat.diffuseTexture = meadowTex;
  (meadowMat.diffuseTexture as Texture).uScale = 4;
  (meadowMat.diffuseTexture as Texture).vScale = 4;
  meadow.material = meadowMat;
  meadow.receiveShadows = true;
  meadow.checkCollisions = true;
  allMeshes.push(meadow);

  // =========================================================================
  // 10. NW meadow in a forest clearing
  // =========================================================================
  const meadowNw = MeshBuilder.CreateGround(
    'meadow-nw',
    { width: 16, height: 14, subdivisions: 4 },
    scene
  );
  meadowNw.position.set(-35, 0.02, 18);
  meadowNw.parent = parent;
  const meadowNwMat = makeMat(scene, 'meadow-nw-mat');
  meadowNwMat.diffuseTexture = meadowTex;
  (meadowNwMat.diffuseTexture as Texture).uScale = 2;
  (meadowNwMat.diffuseTexture as Texture).vScale = 2;
  meadowNw.material = meadowNwMat;
  meadowNw.receiveShadows = true;
  meadowNw.checkCollisions = true;
  allMeshes.push(meadowNw);

  // =========================================================================
  // 11. Wild-grass transition strip along the northern lake shore
  // =========================================================================
  const transition = MeshBuilder.CreateGround(
    'meadow-transition',
    { width: 80, height: 6, subdivisions: 4 },
    scene
  );
  transition.position.set(0, 0.015, -8);
  transition.parent = parent;
  const transMat = makeMat(scene, 'meadow-transition-mat');
  transMat.diffuseTexture = wildTex;
  (transMat.diffuseTexture as Texture).uScale = 6;
  (transMat.diffuseTexture as Texture).vScale = 1;
  transition.material = transMat;
  transition.receiveShadows = true;
  transition.checkCollisions = true;
  allMeshes.push(transition);

  // =========================================================================
  // 12. Wild-grass band SE of the keep (transition to the SE meadow)
  // =========================================================================
  const seBand = MeshBuilder.CreateGround(
    'meadow-se-band',
    { width: 30, height: 10, subdivisions: 4 },
    scene
  );
  seBand.position.set(22, 0.015, 8);
  seBand.parent = parent;
  const seBandMat = makeMat(scene, 'meadow-se-band-mat');
  seBandMat.diffuseTexture = wildTex;
  (seBandMat.diffuseTexture as Texture).uScale = 3;
  (seBandMat.diffuseTexture as Texture).vScale = 2;
  seBand.material = seBandMat;
  seBand.receiveShadows = true;
  seBand.checkCollisions = true;
  allMeshes.push(seBand);

  // =========================================================================
  // 13. Wooden docks on the northern lake shore
  // =========================================================================
  buildDock(scene, shadowGen, parent, -20, -12, 4, 4, 0, woodPlankTexture(scene), timberBeamTexture(scene));
  buildDock(scene, shadowGen, parent, 18, -12, 5, 4, 0, woodPlankTexture(scene), timberBeamTexture(scene));

  // =========================================================================
  // 14. Lily pads scattered on the lake surface
  // =========================================================================
  const lilyPositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const lilyRnd = mulberry32(303);
  for (let i = 0; i < 35; i++) {
    lilyPositions.push({
      x: -30 + lilyRnd() * 60,
      z: -75 + lilyRnd() * 50,
      scale: 1.5 + lilyRnd() * 1.0,
      rot: lilyRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'lily', lilyTex, lilyPositions, 0.6, 0.6);

  // =========================================================================
  // 15. Flowers on the SE meadow + NW meadow (instanced)
  // =========================================================================
  const flowerPositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const flowerRnd = mulberry32(304);
  for (let i = 0; i < 160; i++) {
    flowerPositions.push({
      x: 28 + (flowerRnd() - 0.5) * 32,
      z: 22 + (flowerRnd() - 0.5) * 26,
      scale: 0.7 + flowerRnd() * 0.7,
      rot: flowerRnd() * Math.PI * 2,
    });
  }
  // NW meadow flowers (sparser)
  for (let i = 0; i < 40; i++) {
    flowerPositions.push({
      x: -35 + (flowerRnd() - 0.5) * 14,
      z: 18 + (flowerRnd() - 0.5) * 12,
      scale: 0.6 + flowerRnd() * 0.5,
      rot: flowerRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'flower', flowerTex, flowerPositions, 0.4, 0.4);

  // =========================================================================
  // 16. Grass blades along forest edges + lake shore (instanced)
  // =========================================================================
  const bladePositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const bladeRnd = mulberry32(404);
  // Northern shore strip
  for (let i = 0; i < 250; i++) {
    bladePositions.push({
      x: -38 + bladeRnd() * 76,
      z: -7 + bladeRnd() * 4,
      scale: 0.8 + bladeRnd() * 0.6,
      rot: bladeRnd() * Math.PI * 2,
    });
  }
  // Path between keep grass and SE meadow
  for (let i = 0; i < 150; i++) {
    bladePositions.push({
      x: 12 + bladeRnd() * 26,
      z: -2 + (bladeRnd() - 0.5) * 8,
      scale: 0.8 + bladeRnd() * 0.6,
      rot: bladeRnd() * Math.PI * 2,
    });
  }
  // NW meadow edge
  for (let i = 0; i < 100; i++) {
    bladePositions.push({
      x: -42 + bladeRnd() * 14,
      z: 12 + bladeRnd() * 12,
      scale: 0.8 + bladeRnd() * 0.6,
      rot: bladeRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(
    scene,
    shadowGen,
    parent,
    'grass-blade',
    bladeTex,
    bladePositions,
    0.3,
    0.3
  );

  // =========================================================================
  // 17. Reeds around the entire lake shore (instanced)
  // =========================================================================
  const reedPositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const reedRnd = mulberry32(505);
  // Northern shore (largest concentration, just south of the beach)
  for (let i = 0; i < 120; i++) {
    reedPositions.push({
      x: -42 + reedRnd() * 84,
      z: -15 + (reedRnd() - 0.5) * 2,
      scale: 0.9 + reedRnd() * 0.7,
      rot: reedRnd() * Math.PI * 2,
    });
  }
  // Around the SW bay
  for (let i = 0; i < 30; i++) {
    reedPositions.push({
      x: -42 + reedRnd() * 4,
      z: -38 + (reedRnd() - 0.5) * 28,
      scale: 0.8 + reedRnd() * 0.5,
      rot: reedRnd() * Math.PI * 2,
    });
  }
  // Around the SE bay
  for (let i = 0; i < 25; i++) {
    reedPositions.push({
      x: 26 + reedRnd() * 4,
      z: -42 + (reedRnd() - 0.5) * 22,
      scale: 0.8 + reedRnd() * 0.5,
      rot: reedRnd() * Math.PI * 2,
    });
  }
  // Around the island
  for (let i = 0; i < 20; i++) {
    const ang = reedRnd() * Math.PI * 2;
    reedPositions.push({
      x: -4 + Math.cos(ang) * 5.5,
      z: -32 + Math.sin(ang) * 3.5,
      scale: 0.7 + reedRnd() * 0.4,
      rot: reedRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'reed', reedTex, reedPositions, 0.3, 0.6);

  // =========================================================================
  // 18. Ferns in the forests (instanced)
  // =========================================================================
  const fernPositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const fernRnd = mulberry32(606);
  for (let i = 0; i < 80; i++) {
    fernPositions.push({
      x: -55 + fernRnd() * 50,
      z: -10 + fernRnd() * 40,
      scale: 0.6 + fernRnd() * 0.5,
      rot: fernRnd() * Math.PI * 2,
    });
  }
  for (let i = 0; i < 50; i++) {
    fernPositions.push({
      x: 20 + fernRnd() * 35,
      z: -5 + fernRnd() * 35,
      scale: 0.6 + fernRnd() * 0.5,
      rot: fernRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'fern', fernTex, fernPositions, 0.4, 0.5);

  // =========================================================================
  // 19. Mushrooms in the forests (instanced)
  // =========================================================================
  const mushPositions: Array<{ x: number; z: number; scale?: number; rot?: number }> = [];
  const mushRnd = mulberry32(707);
  for (let i = 0; i < 70; i++) {
    mushPositions.push({
      x: -55 + mushRnd() * 50,
      z: -10 + mushRnd() * 40,
      scale: 0.7 + mushRnd() * 0.5,
      rot: mushRnd() * Math.PI * 2,
    });
  }
  for (let i = 0; i < 40; i++) {
    mushPositions.push({
      x: 20 + mushRnd() * 35,
      z: -5 + mushRnd() * 35,
      scale: 0.7 + mushRnd() * 0.5,
      rot: mushRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'mushroom', mushroomTex, mushPositions, 0.2, 0.25);

  // =========================================================================
  // 20. Rocks scattered throughout the world
  // =========================================================================
  placeRocks(scene, shadowGen, parent, rockTex, [
    // northern shore
    { x: -30, z: -8, w: 0.7, h: 0.5, d: 0.6, rot: 0.4 },
    { x: -12, z: -9, w: 0.5, h: 0.4, d: 0.7, rot: -0.3 },
    { x: 6, z: -9, w: 0.9, h: 0.6, d: 0.7, rot: 0.7 },
    { x: 25, z: -8, w: 0.6, h: 0.5, d: 0.6, rot: 0 },
    // lake shore SW bay
    { x: -44, z: -28, w: 1.2, h: 0.8, d: 1.0, rot: 0.5 },
    { x: -48, z: -42, w: 0.9, h: 0.7, d: 0.9, rot: -0.4 },
    { x: -52, z: -55, w: 1.5, h: 1.0, d: 1.2, rot: 0.2 },
    // lake shore SE bay
    { x: 46, z: -32, w: 0.7, h: 0.5, d: 0.6, rot: 0.6 },
    { x: 50, z: -48, w: 0.9, h: 0.6, d: 0.7, rot: -0.5 },
    // forest NW edge
    { x: -22, z: 16, w: 1.0, h: 0.7, d: 0.9, rot: 0.1 },
    { x: -38, z: 28, w: 0.8, h: 0.6, d: 0.7, rot: -0.7 },
    { x: -55, z: 14, w: 1.1, h: 0.8, d: 1.0, rot: 0.4 },
    // forest NE edge
    { x: 28, z: 18, w: 0.9, h: 0.6, d: 0.8, rot: -0.5 },
    { x: 42, z: 24, w: 1.2, h: 0.8, d: 1.0, rot: 0.3 },
    // SE meadow edge
    { x: 45, z: 18, w: 1.0, h: 0.7, d: 0.9, rot: 0.1 },
    { x: 12, z: 28, w: 0.8, h: 0.6, d: 0.7, rot: -0.7 },
    // around the island
    { x: -8, z: -34, w: 0.6, h: 0.4, d: 0.5, rot: 0.3 },
    { x: 2, z: -29, w: 0.5, h: 0.4, d: 0.6, rot: -0.4 },
  ]);

  // =========================================================================
  // 21. Fallen logs in the forests
  // =========================================================================
  placeLog(scene, shadowGen, parent, logTex, -32, 6, 3.2, 0.3);
  placeLog(scene, shadowGen, parent, logTex, -48, 22, 2.8, -0.7);
  placeLog(scene, shadowGen, parent, logTex, -25, 30, 3.5, 0.9);
  placeLog(scene, shadowGen, parent, logTex, 28, 12, 3.0, -0.4);
  placeLog(scene, shadowGen, parent, logTex, 38, 28, 2.6, 0.6);
  placeLog(scene, shadowGen, parent, logTex, -55, 2, 3.4, 1.1);
  placeLog(scene, shadowGen, parent, logTex, -18, 38, 2.9, -0.2);
  placeLog(scene, shadowGen, parent, logTex, 50, 10, 3.3, 0.5);

  // =========================================================================
  // 22. Standing stones (menhirs) as landmarks
  // =========================================================================
  placeMenhir(scene, shadowGen, parent, menhirTex, -30, 35, 2.5, 0.2);
  placeMenhir(scene, shadowGen, parent, menhirTex, 38, 42, 3.0, -0.4);
  placeMenhir(scene, shadowGen, parent, menhirTex, -55, -10, 2.2, 0.7);

  // =========================================================================
  // 23. Wildlife (deer) scattered in forest clearings
  // =========================================================================
  const deerPositions: Array<{ x: number; z: number; rot?: number }> = [];
  const deerRnd = mulberry32(808);
  for (let i = 0; i < 8; i++) {
    const ang = deerRnd() * Math.PI * 2;
    const r = 28 + deerRnd() * 18;
    const cx = Math.cos(ang) * r;
    const cz = Math.sin(ang) * r;
    // Skip if too close to courtyard
    if (Math.abs(cx) < 14 && cz > -10 && cz < 14) continue;
    deerPositions.push({ x: cx, z: cz, rot: deerRnd() * Math.PI * 2 });
  }
  // A few near the lake
  for (let i = 0; i < 4; i++) {
    deerPositions.push({
      x: -38 + deerRnd() * 8,
      z: -22 + deerRnd() * 6,
      rot: deerRnd() * Math.PI * 2,
    });
  }
  const deerMesh = placeDeer(scene, parent, deerTex, deerPositions);
  for (const m of deerMesh) disposed.push(m);

  // =========================================================================
  // 24. Birds high in the sky (silhouette billboards)
  // =========================================================================
  const birdPositions: Array<{ x: number; z: number; y: number; scale?: number; rot?: number }> = [];
  const birdRnd = mulberry32(909);
  for (let i = 0; i < 6; i++) {
    birdPositions.push({
      x: -40 + birdRnd() * 80,
      z: -30 + birdRnd() * 60,
      y: 25 + birdRnd() * 15,
      scale: 1.5 + birdRnd() * 1.0,
      rot: birdRnd() * Math.PI * 2,
    });
  }
  makeFoliageInstances(scene, shadowGen, parent, 'bird', birdTexture(scene), birdPositions, 0.6, 0.4);

  return {
    update(dtSec: number) {
      lake.update(dtSec);
    },
    dispose() {
      for (const d of disposed) d.dispose();
      for (const m of allMeshes) m.dispose();
    },
  };
}
